import { useTranslation } from "react-i18next";

import { Modal } from "@/components";
import { Radio } from "@/components/Radio/Radio";
import { AddToListMode } from "@/enum/list.enum";
import { SelectionUnit } from "@/utils/transferResultMessage";
import { useSingleFlight } from "@/utils/useSingleFlight";

/** The destination picked in "Add to List": an existing list, or a new one by name. */
export interface PendingTransfer {
	type: "existing" | "new";
	listId?: number;
	listName: string;
}

interface AddToListConfirmModalProps {
	pending: PendingTransfer;
	/** How many the selection covers; the confirm question names it, the result does not. */
	selectionCount: number;
	/** What the selection counts: contacts, or companies whose contacts are added. */
	selectionUnit?: SelectionUnit;
	/** The copy/move choice, offered only where a move exists (a list's own table). */
	modeChoice?: { mode: AddToListMode; onChange: (mode: AddToListMode) => void };
	onClose: () => void;
	onConfirm: () => Promise<void>;
}

const CONFIRM_QUESTION_KEYS: Record<SelectionUnit, { existing: string; new: string }> = {
	contacts: {
		existing: "contacts.confirmAddToListMessage",
		new: "contacts.confirmCreateAndAddToListMessage",
	},
	companies: {
		existing: "contacts.confirmAddCompaniesToListMessage",
		new: "contacts.confirmCreateAndAddCompaniesToListMessage",
	},
};

/**
 * The "Add to List" confirmation of the list, Contacts, Companies and Competitors tables.
 * Confirming runs once however often it is clicked (apps/web CLAUDE.md §24c), and the dialog
 * cannot be dismissed while it runs: Cancel, Escape and the backdrop would hide it while the write
 * goes on, and its result message with it (PREC-4595).
 */
export const AddToListConfirmModal = ({
	pending,
	selectionCount,
	selectionUnit = "contacts",
	modeChoice,
	onClose,
	onConfirm,
}: AddToListConfirmModalProps) => {
	const { t } = useTranslation();
	const confirm = useSingleFlight();
	const isNew = pending.type === "new";

	return (
		<Modal
			isOpened
			handleClose={() => {
				if (!confirm.isRunning) {
					onClose();
				}
			}}
			handleSave={() => confirm.run(onConfirm)}
			isLoading={confirm.isRunning}
			size="xs"
			submitButtonText={t("basics.confirm")}
			title={isNew ? t("contacts.confirmCreateAndAddToList") : t("contacts.confirmAddToList")}
		>
			<div className="text-sm text-text-meta">
				{t(CONFIRM_QUESTION_KEYS[selectionUnit][isNew ? "new" : "existing"], {
					count: selectionCount,
					name: pending.listName,
				})}
			</div>
			{modeChoice && (
				<div className="mt-4 flex flex-col items-start gap-3">
					<Radio
						isChecked={modeChoice.mode === AddToListMode.COPY}
						testId="add-to-list-mode-copy"
						onChange={() => modeChoice.onChange(AddToListMode.COPY)}
					>
						<div className="flex flex-col">
							<span className="text-sm text-text-primary">{t("contacts.addToListModeKeep")}</span>
							<span className="text-xs text-text-meta">
								{t("contacts.addToListModeKeepDescription")}
							</span>
						</div>
					</Radio>
					<Radio
						isChecked={modeChoice.mode === AddToListMode.MOVE}
						testId="add-to-list-mode-move"
						onChange={() => modeChoice.onChange(AddToListMode.MOVE)}
					>
						<div className="flex flex-col">
							<span className="text-sm text-text-primary">{t("contacts.addToListModeMove")}</span>
							<span className="text-xs text-text-meta">
								{t("contacts.addToListModeMoveDescription")}
							</span>
						</div>
					</Radio>
				</div>
			)}
		</Modal>
	);
};
